import axios from "axios";
import {getBackendURL, httpResponse, httpResponseError} from "../../fn";
import {hasStatus} from "./status";
import {getKUVDParams, getRREvents} from "./rrevents";
import {loadDocRefs} from "./documents";
import {RegistrationTypes} from "../../enums";

/**
 * @typedef Subject
 * @type {Object}
 * @property {number} id
 */

/**
 * @typedef Participant
 * @type {Object}
 * @property {number} participantId
 * @property {string} createAt
 * @property {string} role
 * @property {string} signature
 * @property {uuid} signProcessId
 * @property {string} type
 * @property {string} lastName
 * @property {string} firstName
 * @property {string} middleName
 * @property {string} birthDate
 * @property {string} phone
 * @property {string} email
 * @property {string} snils
 * @property {string} gender
 * @property {string} IDDocumentType
 * @property {string} number
 * @property {string} series
 * @property {string} issuer
 * @property {string} issueDate
 * @property {string} divisionCode
 * @property {string} birthPlace
 * @property {string} citizenship
 * @property {string} registrationFiasCode
 * @property {string} registrationRegionCode
 * @property {string} registrationFullAddress
 * @property {string} inn
 * @property {string} kpp
 * @property {string} name
 * @property {string} ogrn
 * @property {string} regDate
 * @property {Object} addressData
 * @property {string} manualFullAddress
 * @property {Array} documentsRef
 * @property {string} rightKind
 * @property {string} share
 * @property {number} representativeId
 * @property {number} representativeDocumentId
 * }
 */

/**
 * @typedef Realty
 * @type {Object}
 * @property {number} id
 * @property {string} type
 * @property {string} cadNum
 * @property {string} fiasCode
 * @property {string} fullAddress
 * @property {string} manualFullAddress
 * @property {AdressData} address
 * @property {string} regionCode
 * @property {string} specification
 * @property {string} square
 */

/**
 * @typedef DealType
 * @type {Object}
 * @property {number} id
 * @property {string} createdAt
 * @property {string} title
 * @property {string} uuid
 * @property {string} type
 * @property {number} realtyId
 * @property {number} registerProcessId
 * @property {number} parentId
 * @property {string|null} extApp
 * @property {boolean} dealWithMortgage
 * @property {number} userid
 * @property {string} branch
 * @property {boolean|null} isBlockedForRegistration
 * @property {boolean|null} periodOfSuspension
 */

/**
 * @typedef AdressData
 * @type {Object}
 * @property {string} ID
 * @property {string} createdAt
 * @property {string} updatedAt
 * @property {string} deletedAt
 * @property {string} query
 * @property {string} value
 * @property {string} unrestrictedValue
 * @property {string} PostalCode
 * @property {string} Country
 * @property {string} CountryISOCode
 * @property {string} FederalDistrict
 * @property {string} RegionFiasID
 * @property {string} RegionKladrID
 * @property {string} RegionISOCode
 * @property {string} RegionWithType
 * @property {string} RegionType
 * @property {string} RegionTypeFull
 * @property {string} Region
 * @property {string} AreaFiasID
 * @property {string} AreaKladrID
 * @property {string} AreaWithType
 * @property {string} AreaType
 * @property {string} AreaTypeFull
 * @property {string} Area
 * @property {string} CityFiasID
 * @property {string} CityKladrID
 * @property {string} CityWithType
 * @property {string} CityType
 * @property {string} CityTypeFull
 * @property {string} City
 * @property {string} CityArea
 * @property {string} CityDistrictFiasID
 * @property {string} CityDistrictKladrID
 * @property {string} CityDistrictWithType
 * @property {string} CityDistrictType
 * @property {string} CityDistrictTypeFull
 * @property {string} CityDistrict
 * @property {string} SettlementFiasID
 * @property {string} SettlementKladrID
 * @property {string} SettlementWithType
 * @property {string} SettlementType
 * @property {string} SettlementTypeFull
 * @property {string} Settlement
 * @property {string} StreetFiasID
 * @property {string} StreetKladrID
 * @property {string} StreetWithType
 * @property {string} StreetType
 * @property {string} StreetTypeFull
 * @property {string} Street
 * @property {string} SteadFiasID
 * @property {string} SteadCadnum
 * @property {string} SteadType
 * @property {string} SteadTypeFull
 * @property {string} Stead
 * @property {string} HouseFiasID
 * @property {string} HouseKladrID
 * @property {string} HouseCadnum
 * @property {string} HouseFlatCount
 * @property {string} House
 * @property {string} HouseType
 * @property {string} HouseTypeFull
 * @property {string} BlockType
 * @property {string} BlockTypeFull
 * @property {string} Block
 * @property {string} Entrance
 * @property {string} Floor
 * @property {string} FlatFiasID
 * @property {string} FlatCadnum
 * @property {string} FlatType
 * @property {string} FlatTypeFull
 * @property {string} Flat
 * @property {string} FlatArea
 * @property {string} SquareMeterPrice
 * @property {string} FlatPrice
 * @property {string} PostalBox
 * @property {string} FiasID
 * @property {string} FiasCode
 * @property {string} FiasLevel
 * @property {string} FiasActualityState
 * @property {string} KladrID
 * @property {string} GeonameID
 * @property {string} CapitalMarker
 * @property {string} GeoLat
 * @property {string} GeoLon
 * @property {string} Okato
 * @property {string} Oktmo
 * @property {string} TaxOffice
 * @property {string} TaxOfficeLegal
 * @property {string} Timezone
 * @property {string} BeltwayHit
 * @property {string} BeltwayDistance
 * @property {string} QCGeo
 * @property {string} QCComplete
 * @property {string} QCHouse
 * @property {string} UnparsedParts
 * @property {string} Source
 * @property {string} QC
 *
 */

/**
 * @typedef StatementStatus
 * @type {Object}
 * @property {number} id
 * @property {string} createdAt
 * @property {string} dealId
 * @property {string} status
 */

/**
 * @typedef UploadFileResult
 * @type {Object}
 * @property {string} ok
 * @property {string} link
 * @property {string} filename
 * @property {number} id
 * @example {
 *  "ok": true,
 *  "link": "https://cdn.esoft.digital/content/cluster/rosreestr/e1/48ba38d857fdbe34c799d8db812d7d5a270ea1e1.png",
 *  "filename": "Screenshot from 2024-08-28 08-59-25.png",
 *  "id": 38
 * }
 */

/**
 * @typedef UploadSigFileResult
 * @type {Object}
 * @property {string} ok
 * @property {string} singlink
 */

/**
 * Массив значений селекта
 * @typedef SelectData
 * @type {Object}
 * @property {string} key
 * @property {string} value
 */

/**
 * @typedef DealDocument
 * @type {Object}
 * @property {string} id
 * @property {string} createdAt
 * @property {string} dealId
 * @property {string} fileName
 * @property {string} fileDesc
 * @property {string} link
 * @property {string} type
 * @property {string} subtype
 * @property {string} issuer
 * @property {string} issueDate
 * @property {string} series
 * @property {string} number
 * @property {string} comment
 * @property {string} deleted
 * @property {string} hasSignature
 * @property {string} signature
 * @property {string} signlink
 * @property {string} signAt
 * @property {string} sendToSignAt
 * @property {string} sentForSignTo
 * @property {string} singlink
 * @property {string} extsigner
 * @property {string} extSignStatus
 */

/**
 * Все данные по сделке
 * @type {{
 *  dealId: number,
 *  type: string,
 *  deal: DealType,
 *  realty: Realty,
 *  participants: [Participant]
 *  documents: [DealDocument],
 *  internalSignersList: [SelectData],
 *  internalSignersList: [SelectData],
 *  statementStatus: StatementStatus,
 *  statuses: [],
 *  rrEvents,
 *  childDeals: [],
 *  xmlGenLock: boolean
 *  childDeals: [],
 *  configs: {}
 * }}
 *
 */
export let dealData = {
  dealId: 0,
  type: '',
  deal: {
    id: 0,
    createdAt: '',
    title: '',
    uuid: '',
    type: '',
    statementType: null,
    realtyId: 0,
    registerProcessId: 0,
    parentId: null,
    extApp: null,
    dealWithMortgage: false,
    userid: 0,
    branch: '',
    isBlockedForRegistration: null,
    agreementType: '',
    comment:''
  },
  realty: {
    id: 0,
    type: '',
    cadNum: '',
    fiasCode: '',
    fullAddress: '',
    manualFullAddress: '',
    address: {},
    regionCode: '',
    specification: null,
    square: ''
  },
  documents: [],
  participants: [],
  user: {
    id: 0,
    fio: '',
    phone: '',
    cityid: '',
    email: '',
    photo: ''
  },
  dealXmlStatementsList: [],
  internalSignersList: [],
  signatureInfo: [],
  generatedDocuments: [],
  statuses: [],
  rrEvents: [],
  documentsRefListToParticipants: [],
  xmlGenLock: null,
  childDeals: [],
  configs: {
    guarantors: [],
    isMyDSSDisabled: false
  }
};

export const dealLoad = async (id) => {
  const result = await new Promise((resolve) => {
    axios.get(`${getBackendURL()}/deal/${id}`, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error))
    });
  })

  if (result?.data?.id) {
    dealData = result.data;
    dealData.dealId = result.data.id;
    dealData.statuses = result.data.status;
    dealData.documentsRefListToParticipants = [];

    loadDocRefs();

    if (hasStatus('dealSentToRR')) {
      await getRREvents(dealData.dealId)
    }
  }

  return result;
}

/**
 * Отправить сделку в Росреестр
 * @param dealId
 * @returns {Promise<unknown>}
 */
export const sendToRR = async (dealId) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/deal/${dealId}/send/rosreestr`, {},{
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch((error) => {
      resolve(httpResponseError(error));
    });
  })

  return result;
}

/**
 * Запрос на генерацию ХМЛ заявлений
 * @returns {Promise<HttpResponseOk|HttpResponseError>}
 */
export const generateXml = async (dealId) => {
  const result = await new Promise((resolve) => axios.post(`${getBackendURL()}/deal/${dealId}/generatexml`, {}, {
    withCredentials: true,
    headers: {
      'content-type': 'application/json',
    },
  })
    .then(function (response) {
      if (response.status === 200) {
        resolve(httpResponse(response.data))
      }
    })
    .catch(function (error) {
      resolve(httpResponseError(error));
    }));

  return result;
}

/**
 * Сохранить дополнительные параметры по сделке
 * @param values
 * @returns {Promise<void>}
 */
export const saveDealParams = async (values) => {
  const result = await new Promise((resolve) => {
    axios.put(`${getBackendURL()}/deal/${dealData.dealId}/save/values`, values, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(function (response) {
        resolve(httpResponse({}))
      })
      .catch(function (error) {
        resolve(httpResponseError(error));
      });
  });

  return result;
}

/**
 * Создание новой сделки
 * @returns {Promise<unknown>}
 */
export const createNewDeal = async (type) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/deal/create`, {type}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch(function (error) {
      resolve(httpResponseError(error));
    });
  })

  if (result?.ok) {
    dealData.dealId = result.data.id;
    dealData.deal.type = type;

    const url = new URL(window.location.href);
    url.searchParams.set('id', result.data.id);
    window.history.replaceState(null, null, url);
  }

  return result;
}

/**
 * Создание новой дочерней сделки
 * @returns {Promise<unknown>}
 */
export const createChildDeal = async (dealType, statementType) => {
  if(statementType === RegistrationTypes.REPAYMENT_MORTGAGE_REG_RECORD) {
    statementType = '';
  }

  let values = {};

  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/deal/create/child`, {
      dealType,
      statementType,
      parentId: dealData.dealId,
      values
    }, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then((response) => {
      resolve(httpResponse(response.data))
    }).catch(function (error) {
      resolve(httpResponseError(error));
    });
  })

  return result;
}

/**
 *
 * @param value
 * @returns {Promise<{label: string, id: string, regionCode: string}>}
 */
export const getAddressAutocomplete = async (value) => {
  if (
    !value ||
    typeof value !== 'string' ||
    value.length <= 5
  )
    return [];

  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/deal/addrautocomplete/`, {query: value}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    })
      .then(function (response) {
        if (response.status === 200) {
          resolve(response.data)
        }
      })
      .catch(function (error) {
        const err = httpResponseError(error);
      });
  });

  if (Array.isArray(result)) {
    const adresses = []
    for (const item of result) {
      const regionCode = item.RegionKladrID?.substring(0, 2);
      adresses.push({
        label: item.UnrestrictedValue,
        id: item.FiasID,
        regionCode,
        address: item,
      })
    }

    return adresses;
  }

  return [];
}


/**
 *
 * @returns {Promise<unknown>}
 * @param dealId
 */
export const getLinkedDeals = async (dealId) => {
  const result = await new Promise((resolve) => {
    axios.get(`${getBackendURL()}/deal/${dealId}/linked`, {
      withCredentials: true,
      headers: {
        'content-type': 'multipart/form-data',
      },
    }).then(function (response) {
      if (response.status === 200) {
        resolve(httpResponse(response.data));
      }
    })
      .catch(function (error) {
        resolve(httpResponseError(error));
      });
  })

  return result;
};

/**
 *
 * @returns {Promise<unknown>}
 * @param childDealIds
 * @param parentDealId
 * @param childDealIds
 */
export const transferDealData = async (parentDealId, childDealIds) => {
  const result = await new Promise((resolve) => {
    axios.post(`${getBackendURL()}/deal/transfer_data`, {parentDealId, childDealIds}, {
      withCredentials: true,
      headers: {
        'content-type': 'application/json',
      },
    }).then(function (response) {
      if (response.status === 200) {
        resolve(httpResponse(response.data));
      }
    })
      .catch(function (error) {
        resolve(httpResponseError(error));
      });
  })

  return result;
};
